import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount, useContract, useSigner } from 'wagmi';

import {
  cancelOrder,
  getClosedOrders,
  subscribeOpenOrders,
  getAllOrders,
  downloadOrdersCsv,
} from 'store/entities/trade/orders/thunks';
import { addNotification } from 'store/entities/notification/slice';
import { setCurrentHistoryPage } from 'store/entities/trade/orders/slice';
import {
  selectOpenOrders,
  selectClosedOrders,
  selectClosedOrdersLoading,
  selectClosedOrdersCount,
  selectOpenOrdersLoading,
  selectCancelOrderLoading,
  selectCurrentHistoryPage,
} from 'store/entities/trade/orders/selectors';
import { isCountryBlacklistedSelector } from 'store/entities/config/selectors';
import { selectContracts } from 'store/entities/market/selectors';

import { getOrdersTabOptions, getNotification } from 'helpers';

import escrowConAbi from 'config/contractsABI/escrowAbi.json';
import { signCancelOrder } from 'utils/smartcontract/orderServices';

import OrdersTable from 'components/ordersTable';
import Tabs from 'elements/tabs';
import CancelOrderModal from './cancelOrderModal/CancelOrderModal';
import Pagination from 'elements/pagination/Pagination';

import { 
  NotificationType,
  OrdersViewTabs,
  Notifications,
  OrdersTabT,
  OrderT,
} from 'types';

import DownloadIcon from 'assets/images/download-icon.svg';
import ReloadIcon from 'assets/images/reload-icon.svg';

import './MainViewOrders.scss';

const MainViewOrders = () => {
  const dispatch = useDispatch();

  const { isConnected, address } = useAccount();
  const { data: signer } = useSigner();

  const contracts = useSelector(selectContracts);
  const isForbiddenRegion: boolean = useSelector(isCountryBlacklistedSelector);
  const openOrders = useSelector(selectOpenOrders);
  const closedOrders = useSelector(selectClosedOrders);
  const isClosedOrdersLoading = useSelector(selectClosedOrdersLoading);
  const isOpenOrdersLoading = useSelector(selectOpenOrdersLoading);
  const isCancelOrderLoading = useSelector(selectCancelOrderLoading);
  const [isCancelSignOrderLoading, setCancelSignOrderLoading] = useState(false);

  const tabsOptions: OrdersTabT[] = getOrdersTabOptions(_t);
  const [currentTab, setCurrentTab] = useState(tabsOptions[0]);
  const [isCancelOrderModalShown, setCancelOrderModalShown] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderT | null>(null);

  const closedOrdersCount = useSelector(selectClosedOrdersCount);
  const currentHistoryPage = useSelector(selectCurrentHistoryPage);
  const pageSize = 10;

  const escrowContract = useContract({
    address: contracts?.escrow || '',
    abi: escrowConAbi,
    signerOrProvider: signer,
  });

  const getAllOrdersHandler = () => {
    if (address) {
      dispatch(getAllOrders(address));
    }
  };

  useEffect(() => {
    if (address && signer) {
      dispatch(getClosedOrders({ address }));
      dispatch(subscribeOpenOrders(address));
    }
  }, [isConnected, signer]);

  const refreshOrders = () => {
    if (!address || !isConnected) {
      dispatch(
        addNotification({
          type: NotificationType.Canceled,
          msg: getNotification(Notifications.PleaseConnectWallet, _t),
        })
      );
      return;
    }
    getAllOrdersHandler();
  };

  const downloadCsv = () => {
    if (!address || !isConnected) {
      dispatch(
        addNotification({
          type: NotificationType.Canceled,
          msg: getNotification(Notifications.PleaseConnectWallet, _t),
        })
      );
      return;
    }

    dispatch(downloadOrdersCsv({ address, type: currentTab.value }));
  };

  const submitCancelOrder = async (orderData: OrderT, isReclaim?: boolean) => {
    const order = orderData ? orderData : selectedOrder;

    if (address && escrowContract && order) {
      setCancelSignOrderLoading(true);
      setCancelOrderModalShown(false);

      let tx;
      try {
        tx = await signCancelOrder(escrowContract, order.escrow_id);
      } catch (error) {
        if (isReclaim) {
          dispatch(
            addNotification({
              type: NotificationType.Canceled,
              msg: getNotification(Notifications.ReclaimOrderError, _t),
            })
          );
        } else {
          dispatch(
            addNotification({
              type: NotificationType.Canceled,
              msg: getNotification(Notifications.CancelOrderError, _t),
            })
          );
        }
      }

      if (tx) {
        dispatch(cancelOrder({ id: order.id, address, isReclaim: isReclaim || false }));
      }

      setCancelSignOrderLoading(false);
    }
  };

  const handlerCancelOrder = (order: OrderT, isReclaim: boolean) => {
    if (isForbiddenRegion) return;

    setSelectedOrder(order);

    if (isReclaim) {
      submitCancelOrder(order, isReclaim);
    } else {
      setCancelOrderModalShown(true);
    }
  };

  const changePageHandler = (page: number) => {
    if (page === currentHistoryPage) return;

    if (address) {
      const pageOffset = pageSize * page - pageSize;

      dispatch(getClosedOrders({ address, offset: pageOffset, limit: pageSize }));
      dispatch(setCurrentHistoryPage(page));
    }
  };

  const isWithoutPagination = !isConnected || (currentHistoryPage === 1 && closedOrdersCount < pageSize);

  return (
    <div className="main-view-orders">
      <div className="main-view-orders-header">
        <div className="main-view-orders-tabs">
          <Tabs data={tabsOptions} currentTab={currentTab} handlerClick={(tab: OrdersTabT) => setCurrentTab(tab)} />
          <div className="main-view-orders-buttons">
            <b onClick={downloadCsv} className="main-view-orders-buttons-download">
              <img src={DownloadIcon} alt="download" />
            </b>
            <b onClick={refreshOrders} className="main-view-orders-buttons-reload">
              <img src={ReloadIcon} alt="reload" />
            </b>
          </div>
        </div>
      </div>
      <div className={`main-view-orders-content ${isWithoutPagination ? 'withoutPagination' : ''}`}>
        <OrdersTable
          currentTab={currentTab?.value}
          orders={currentTab.value === OrdersViewTabs.OpenOrders ? openOrders : closedOrders}
          handlerCancelOrder={handlerCancelOrder}
          isConnected={isConnected}
          isOrdersLoading={currentTab.value === OrdersViewTabs.OpenOrders ? isOpenOrdersLoading : isClosedOrdersLoading}
          isCancelOrderLoading={isCancelOrderLoading}
          isCancelSignOrderLoading={isCancelSignOrderLoading}
          selectedOrderId={selectedOrder?.id}
        />
        {currentTab.value === OrdersViewTabs.OrderHistory && isConnected && (
          <Pagination
            className="orders-pagination"
            pageSize={pageSize}
            currentPage={currentHistoryPage}
            totalCount={closedOrdersCount}
            onPageChange={changePageHandler}
          />
        )}
      </div>

      <CancelOrderModal
        onSubmit={submitCancelOrder}
        onClose={setCancelOrderModalShown}
        show={isCancelOrderModalShown}
      />
    </div>
  );
};

export default MainViewOrders;
